import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { QueryResult } from '@buttress/crag';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';
import Company from '../../../../models/company.js';
import Person from '../../../../models/person.js';

import { callLambda } from '../../../../helpers/lambda.js';
import { LiveAnnouncer } from '../../../../helpers/live-announcer.js';
import { nsPersonRowStyles } from '../../../../ns-styles/index.js';

import '../../../../ns-icon/ns-icon.js';
import '../../../../ns-elements/ns-job-layout.js';
import definition from '@job-schemas/company/verify-staff-claims.json';
import { jobSchema } from '../../job-schema.js';

type ClaimType = 'DIRECTORS_VERIFICATION' | 'PAYMENT_APPROVERS_VERIFICATION' | 'SUBORDINATES_VERIFICATION';

@customElement(NSVerifyStaffClaims.is)
export default class NSVerifyStaffClaims extends NSJobComponent {
  static get is() {
    return 'ns-verify-staff-claims';
  }

  @state()
  private __isProcessedJob: boolean = false;

  @state()
  private __type?: ClaimType;

  @state()
  private __staff?: Person[];

  @state()
  private __company?: Company;

  @state()
  private __list?: {
    id: string;
    status: string;
  }[];

  private __announcer = new LiveAnnouncer(this);

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'button', label: 'Complete' } } });
  }

  static styles = [
    ...super.styles,
    nsPersonRowStyles,
    css`
      .company-name {
        color: var(--ns-theme-purple);
        font-weight: 600;
      }

      .person-row {
        grid-template-columns: auto minmax(0, 1.2fr) minmax(0, 1.4fr) minmax(0, 1.2fr) auto;
      }
      .person-row__actions {
        gap: var(--wa-space-2xs);
      }
      /* Ticked: the purple tick on the green fill, which a white one is too faint against */
      .person-row__action--verify[aria-pressed='true'] {
        background: var(--ns-theme-green);
        color: var(--ns-theme-purple);
      }
      .person-row__action--reject[aria-pressed='true'] {
        background: var(--ns-theme-red);
      }
      .status-badge--rejected {
        background: var(--ns-theme-red);
      }
    `,
  ];

  async connectedCallback() {
    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._directory) throw new Error('Unable to find NSDirectoryService');

      if (!this._auth) throw new Error('Unable to find auth service');

      this.__company = this._directory.company;

      if (!this._job) throw new Error('Unable to find job card');

      if (this._job.metadata.findIndex((m) => m.key === 'PROCESSED' && m.value === 'TRUE') !== -1) {
        this.__isProcessedJob = true;
      }

      const staff = this._job.inputValues.staff;
      const staffIds = Array.isArray(staff) ? staff : [staff];
      const type = this._job.inputValues.type;

      const peopleQueryCall: QueryResult = await this._db.query('people', {
        id: {
          $in: staffIds,
        },
      });
      this.__staff = peopleQueryCall.results as unknown[] as Person[];
      if (!this.__staff || this.__staff.length < 1) throw new Error('Missing required staff');
      if (!this.__staff.every((p) => staffIds.includes(p.id))) throw new Error('Could not find all the staff');

      this.__type = type as ClaimType;

      // Getting verified and rejected staff from job metadata
      const list = this.getMetadataValue('LIST');
      this.__list = list ? JSON.parse(list) : [];
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading companies');
    }
  }

  async _outcomeCompleteHandler() {
    // Another director has already sent the verifications, so there's nothing to send again
    if (this.__isProcessedJob) {
      this.outcome('complete');
      return;
    }

    try {
      if (!this._db) throw new Error('Missing required db service');
      if (!this._auth) throw new Error('Missing required auth service');
      if (!this._job) throw new Error('Missing required job');

      this.loading('Updating the data accordingly');
      await callLambda(
        'company/verify-staff-claims',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          staffList: this.__list,
        }),
      );
      this.loaded();
      this.outcome('complete');
    } catch (err: any) {
      this.loaded();
      throw new Error(
        err.message
          ? err.message
          : 'Something went wrong, our support is working on fixing it and they will contact you',
      );
    }
  }

  private __action(id: string, type: string) {
    if (!this.__staff) throw new Error('Missing required staff');

    const person = this.__staff.find((s) => s.id === id);
    if (!person) throw new Error(`Could not find member of staff with ID: ${id}`);

    const list = this.getMetadataValue('LIST');
    let updatedList: {
      id: string;
      status: string;
    }[] = [];
    let index = -1;
    if (list) {
      updatedList = JSON.parse(list);
      index = updatedList.findIndex((i) => i.id === id);
    }
    if (index === -1) {
      updatedList.push({
        id,
        status: type,
      });
    } else {
      updatedList[index].status = type;
    }

    this.__list = updatedList;
    this.updateMetadata('LIST', JSON.stringify(updatedList));

    const name = `${person.forename} ${person.surname}`.trim();
    this.__announcer.announce(`${name} marked as ${type === 'VERIFY' ? 'verified' : 'rejected'}.`);
  }

  private __renderIntro() {
    if (this.__isProcessedJob) {
      return 'Another director has already verified the below. You can just complete the job, thanks!';
    }

    const company = this.__company?.name ?? 'your company';
    if (this.__type === 'DIRECTORS_VERIFICATION') {
      // On separate lines, the full stop would get a space before it
      // prettier-ignore
      return html`The below staff claimed that they are directors of <strong class="company-name">${company}</strong>.`;
    }
    if (this.__type === 'PAYMENT_APPROVERS_VERIFICATION') {
      // On separate lines, the full stop would get a space before it
      // prettier-ignore
      return html`The below staff claimed that they are payment approvers of <strong class="company-name">${company}</strong>.`;
    }
    if (this.__type === 'SUBORDINATES_VERIFICATION') {
      return 'The below staff claimed that they report to you and that you are their manager.';
    }
    return nothing;
  }

  private __getInitials(name: string): string {
    return name
      .split(' ')
      .filter((part) => part)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }

  private __renderStaff() {
    return html`
      <div class="vertical" role="list" aria-labelledby="verify-intro">
        ${repeat(
          this.__staff ?? [],
          (personnel) => personnel.id,
          (personnel) => {
            const status = this.__list?.find((i) => i.id === personnel.id)?.status;
            const name = `${personnel.forename} ${personnel.surname}`.trim();
            return html`
              <div class="person-row" role="listitem">
                <div class="person-row__avatar horizontal center-center" aria-hidden="true">
                  ${this.__getInitials(name)}
                </div>

                <div class="person-row__cell">
                  <span class="value">${name}</span>
                  <span class="caption">
                    <span class="caption-text">${personnel.jobTitle ?? ''}</span>
                    ${status === 'VERIFY'
                      ? html`
                          <span class="status-badge">Verified</span>
                        `
                      : nothing}
                    ${status === 'REJECT'
                      ? html`
                          <span class="status-badge status-badge--rejected">Rejected</span>
                        `
                      : nothing}
                  </span>
                </div>

                <div class="person-row__cell">
                  <span class="value">${personnel.email}</span>
                  <span class="caption">Email</span>
                </div>

                <div class="person-row__cell">
                  <span class="value">${personnel.jobTitle ?? ''}</span>
                  <span class="caption">Job role</span>
                </div>

                <div class="horizontal person-row__actions">
                  <button
                    type="button"
                    class="person-row__action person-row__action--verify"
                    aria-pressed="${status === 'VERIFY'}"
                    @click=${() => this.__action(personnel.id, 'VERIFY')}
                  >
                    <ns-icon icon="checkmark"></ns-icon>
                    <span class="visually-hidden">Verify ${name}</span>
                  </button>
                  <button
                    type="button"
                    class="person-row__action person-row__action--reject"
                    aria-pressed="${status === 'REJECT'}"
                    @click=${() => this.__action(personnel.id, 'REJECT')}
                  >
                    <ns-icon icon="times"></ns-icon>
                    <span class="visually-hidden">Reject ${name}</span>
                  </button>
                </div>
              </div>
            `;
          },
        )}
      </div>
    `;
  }

  render() {
    return html`
      <ns-job-layout image="/images/jobs/reporting-to.png" image-label="Reporting structure illustration">
        ${this.__announcer.render()}
        <div class="job-panel">
          <p id="verify-intro">${this.__renderIntro()}</p>
          ${this.__isProcessedJob ? nothing : this.__renderStaff()}
        </div>
      </ns-job-layout>
    `;
  }
}
